import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, TrendingDown, Users, Award, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n";
import { useDiscovery } from "@/components/Layout";
import { MaskedLines, Reveal, FadeIn, ParallaxImage, Marquee, Kicker } from "@/components/motion";

const BOARDROOM =
  "https://images.pexels.com/photos/8297484/pexels-photo-8297484.jpeg?auto=compress&cs=tinysrgb&w=1260";

const CONSULTING = "/hero-presentation.jpg";

const blockIcons = [TrendingDown, Users, Award, ShieldCheck];

const Hero = () => {
  const { t } = useLang();
  const h = t.home.hero;
  return (
    <section className="relative overflow-hidden pt-28 pb-8 sm:pt-32 sm:pb-12 grain" data-testid="home-hero">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-sage-100/70 blur-3xl" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-[380px] w-[380px] rounded-full bg-gold-light/25 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:items-center lg:gap-8">
        <div className="lg:col-span-7">
          <FadeIn delay={0.15}>
            <p className="flex items-center gap-4 text-sm font-medium uppercase tracking-[0.3em] text-gold-deep" data-testid="hero-kicker">
              <span className="inline-block h-px w-16 bg-gold" />
              {h.kicker}
            </p>
          </FadeIn>

          <MaskedLines
            className="mt-9 font-serif text-4xl leading-[1.08] tracking-tight text-navy-900 sm:text-5xl lg:text-6xl"
            delay={0.3}
            lines={[
              h.titleA,
              <em key="l2" className="text-sage-700">{h.titleEm}</em>,
            ]}
          />

          <Reveal delay={0.75}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-navy-900/70 sm:text-lg" data-testid="hero-subtitle">
              {h.subtitle}
            </p>
          </Reveal>
        </div>

        <div className="relative lg:col-span-5">
          <FadeIn delay={0.55} className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full border border-gold/50" />
            <ParallaxImage
              src={CONSULTING}
              alt={h.imageAlt}
              testId="hero-image"
              className="relative aspect-[4/3] w-full shadow-2xl"
              speed={0.03}
              position="50% 35%"
            />
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

const MethodPreview = () => {
  const { t } = useLang();
  const open = useDiscovery();
  const m = t.home.method;
  const h = t.home.hero;
  return (
    <section className="pt-10 pb-20 sm:pt-14 sm:pb-28" data-testid="method-preview-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="text-sm font-medium uppercase tracking-[0.3em] text-gold-deep" data-testid="method-kicker">
            {m.kicker}
          </p>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="max-w-2xl font-serif text-2xl tracking-tight text-navy-900 sm:text-3xl lg:text-4xl">{m.title}</h2>
            <Link to="/approccio" data-testid="method-preview-link" className="link-editorial text-sm font-medium text-sage-700">
              {m.link} →
            </Link>
          </div>
        </Reveal>
        <div className="mt-12">
          {m.phases.map((p, i) => (
            <motion.div
              key={i}
              data-testid={`method-phase-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="grid gap-1.5 border-t border-navy-900/10 py-6 sm:grid-cols-12 sm:items-baseline sm:gap-6"
            >
              <span className="font-mono text-sm text-gold-deep sm:col-span-1">{p.n}</span>
              <p className="text-base leading-relaxed text-navy-900/80 sm:col-span-11 sm:text-lg">{p.text}</p>
            </motion.div>
          ))}
          <div className="border-t border-navy-900/10" />
        </div>
        <Reveal className="mt-12 text-center">
          <button
            onClick={open}
            data-testid="method-discovery-call-btn"
            className="group mt-8 inline-flex items-center gap-2.5 bg-navy-900 px-7 py-4 text-sm font-medium tracking-wide text-cream transition-colors duration-300 hover:bg-sage-700"
          >
            {h.ctaPrimary}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

const StatBar = () => {
  const { t } = useLang();
  const s = t.home.stats;
  return (
    <section className="bg-navy-900 py-16 text-cream sm:py-20 grain relative" data-testid="stats-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Kicker light>{s.kicker}</Kicker>
          <h2 className="mt-4 max-w-2xl font-serif text-2xl tracking-tight sm:text-3xl lg:text-4xl">{s.title}</h2>
        </Reveal>
        <div className="mt-12 grid gap-px overflow-hidden border border-cream/10 bg-cream/10 sm:grid-cols-2 lg:grid-cols-4">
          {s.items.map((item, i) => (
            <motion.div
              key={i}
              data-testid={`stat-item-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="group bg-navy-900 p-7 transition-colors duration-500 hover:bg-navy-800"
            >
              <p className="font-mono text-3xl tracking-tight text-gold-light sm:text-4xl">{item.value}</p>
              <p className="mt-3 text-base font-semibold text-cream sm:text-lg">{item.label}</p>
              <p className="mt-4 text-[10px] uppercase tracking-[0.15em] text-cream/40">{item.source}</p>
            </motion.div>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-3xl text-[11px] leading-relaxed text-cream/40" data-testid="stats-footnote">{s.footnote}</p>
        </Reveal>
      </div>
    </section>
  );
};

const BusinessCase = () => {
  const { t } = useLang();
  const open = useDiscovery();
  const bc = t.home.businessCase;
  return (
    <section className="py-20 sm:py-28" data-testid="business-case-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>{bc.kicker}</Kicker>
              <h2 className="mt-4 font-serif text-2xl tracking-tight text-navy-900 sm:text-3xl lg:text-4xl">{bc.title}</h2>
              <p className="mt-5 text-base leading-relaxed text-navy-900/65">{bc.intro}</p>
              <div className="mt-7 border-l-2 border-gold pl-5" data-testid="business-case-highlight">
                <p className="font-mono text-2xl tracking-tight text-navy-900 sm:text-3xl">{bc.highlight.value}</p>
                <p className="mt-2 text-sm leading-relaxed text-navy-900/65">{bc.highlight.text}</p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-navy-900/40">{bc.highlight.source}</p>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="mt-8 hidden lg:block">
              <ParallaxImage
                src={BOARDROOM}
                alt="Strategic boardroom discussion"
                testId="business-case-image"
                className="aspect-[4/3] w-full"
                speed={0.1}
              />
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid gap-px border border-navy-900/10 bg-navy-900/10 sm:grid-cols-2">
              {bc.blocks.map((b, i) => {
                const Icon = blockIcons[i];
                return (
                  <motion.div
                    key={i}
                    data-testid={`business-case-block-${i}`}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                    className="group relative bg-cream p-8 transition-colors duration-500 hover:bg-white"
                  >
                    <div className="flex items-start justify-between">
                      <span className="font-mono text-xs text-gold-deep">0{i + 1}</span>
                      <Icon size={20} className="text-sage-700 transition-transform duration-500 group-hover:-translate-y-1" />
                    </div>
                    <h3 className="mt-5 text-lg font-medium tracking-tight text-navy-900">{b.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-navy-900/65">{b.text}</p>
                    {b.source && (
                      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-navy-900/40">{b.source}</p>
                    )}
                  </motion.div>
                );
              })}
            </div>
            <Reveal delay={0.2} className="mt-8">
              <button
                onClick={open}
                data-testid="business-case-cta-btn"
                className="group flex items-center gap-2.5 bg-sage-700 px-7 py-4 text-sm font-medium tracking-wide text-cream transition-colors duration-300 hover:bg-navy-900"
              >
                {bc.cta}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

const AreasTeaser = () => {
  const { t } = useLang();
  const a = t.home.areas;
  return (
    <section className="bg-sand py-20 sm:py-28" data-testid="areas-teaser-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Kicker>{a.kicker}</Kicker>
            <h2 className="mt-4 max-w-xl font-serif text-2xl tracking-tight text-navy-900 sm:text-3xl lg:text-4xl">{a.title}</h2>
          </div>
          <Link to="/aree-competenza" data-testid="areas-teaser-link" className="link-editorial text-sm font-medium text-sage-700">
            {a.link} →
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-px border border-navy-900/10 bg-navy-900/10 sm:grid-cols-2 lg:grid-cols-3">
          {a.items.map((item, i) => (
            <motion.div
              key={i}
              data-testid={`area-teaser-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
            >
              <Link to="/aree-competenza" className="group block h-full bg-sand p-8 transition-colors duration-500 hover:bg-navy-900">
                <span className="font-mono text-xs text-gold-deep transition-colors group-hover:text-gold">0{i + 1}</span>
                <h3 className="mt-4 font-serif text-xl tracking-tight text-navy-900 transition-colors group-hover:text-cream">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-navy-900/60 transition-colors group-hover:text-cream/60">{item.text}</p>
                <ArrowUpRight size={16} className="mt-5 text-navy-900/30 transition-all duration-300 group-hover:translate-x-1 group-hover:text-gold" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Quote = () => {
  const { t } = useLang();
  return (
    <section className="bg-sage-50 py-20 sm:py-28" data-testid="quote-section">
      <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <span className="font-serif text-6xl leading-none text-gold">“</span>
          <blockquote className="mt-2 font-serif text-2xl italic leading-relaxed tracking-tight text-navy-900 sm:text-3xl" data-testid="quote-text">
            {t.home.quote.text}
          </blockquote>
          <p className="mt-6 text-xs uppercase tracking-[0.28em] text-sage-700">— {t.home.quote.author}</p>
        </Reveal>
      </div>
    </section>
  );
};

const FinalCta = () => {
  const { t } = useLang();
  const open = useDiscovery();
  const f = t.home.finalCta;
  return (
    <section className="relative overflow-hidden bg-navy-900 py-20 text-cream sm:py-28 grain" data-testid="final-cta-section">
      <div className="pointer-events-none absolute -left-32 top-0 h-[400px] w-[400px] rounded-full bg-sage-700/25 blur-3xl" />
      <div className="relative mx-auto max-w-4xl px-5 text-center sm:px-8">
        <Reveal>
          <h2 className="font-serif text-3xl tracking-tight sm:text-4xl lg:text-5xl">{f.title}</h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-cream/65">{f.text}</p>
          <button
            onClick={open}
            data-testid="final-cta-btn"
            className="group mt-9 inline-flex items-center gap-2.5 border border-gold/60 px-8 py-4 text-sm font-medium tracking-wide text-gold-light transition-all duration-300 hover:bg-gold hover:text-navy-900"
          >
            {f.cta}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLang();
  return (
    <>
      <Hero />
      <MethodPreview />
      <StatBar />
      <BusinessCase />
      <Marquee items={t.home.marquee} />
      <AreasTeaser />
      <Quote />
      <FinalCta />
    </>
  );
}
